import Box from "@material-ui/core/esm/Box";
import Dialog from "@material-ui/core/esm/Dialog";
import DialogContent from "@material-ui/core/esm/DialogContent";
import DialogTitle from "@material-ui/core/esm/DialogTitle";
import Paper from "@material-ui/core/esm/Paper";
import CheckCircle from "@material-ui/icons/esm/CheckCircle";
import FiberManualRecord from "@material-ui/icons/esm/FiberManualRecord";
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { SubscriptionsService } from 'api';
import { getObeDetailsRequestIdState, getObeDetails as getObeDetailsSelector } from 'api/obes/getObeDetails';
import { BackButton, PrimaryButton, SecondaryButton } from 'components/Button';
import Spinner from 'components/Spinner/Spinner';
import { Header, LargeHeader, Title } from 'components/Text';
import { FLYT_NO_LINK } from 'constants/urls';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import { TAG_DETAILS } from 'routes/overview/paths';
import { SubscriptionPlan, getSubscriptionPlanId, getSubscriptionPlanName } from 'utils/mapping/mapSubscriptionPlan';
import checkLoadable from 'utils/recoil/checkLoadable';
import useStyles from './ChangeSubscriptionPlanStyles';
import { getAgreementsRequestIdState } from 'api/agreements/getAgreementsAndVehicle';
export default function ChangeSubscriptionPlan() {
  const {
    t
  } = useTranslation();
  const {
    push
  } = useHistory();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    tagNumber
  } = useParams();
  const {
    customer
  } = useCurrentCustomer();
  const {
    container,
    currentPlanBox,
    alternativePlanBox,
    currentPlanBadge,
    confirmDialog,
    planTitle,
    planDescription,
    featuresList,
    featureItem,
    planPrice
  } = useStyles();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const getObeDetails = useRecoilValueLoadable(getObeDetailsSelector(tagNumber));
  const {
    contents: tag,
    loading
  } = checkLoadable(getObeDetails);
  const setObeDetailsRequestState = useSetRecoilState(getObeDetailsRequestIdState);
  const setAgreementsRequestId = useSetRecoilState(getAgreementsRequestIdState);

  // Define features for both plans
  const standardFeatures = ['subscriptions.features.autopass_discount', 'subscriptions.features.ferry_discount', 'subscriptions.features.email_notifications', 'subscriptions.features.full_app_access', 'subscriptions.features.exclusive_benefits', 'subscriptions.features.free_tag_replacement', 'subscriptions.features.no_setup_fee'];
  const basicFeatures = ['subscriptions.features.autopass_discount', 'subscriptions.features.ferry_discount', 'subscriptions.features.email_notifications'];
  if (loading) return /*#__PURE__*/React.createElement(Spinner, null);
  if (!tag) return /*#__PURE__*/React.createElement(Title, {
    align: "center"
  }, t('error.no_tag'));

  // Determine current plan: use pending subscription change if exists, otherwise use OBE subscription plan
  const effectivePlanId = tag.pendingSubscriptionChange?.newSubscriptionPlanId || tag.subscriptionPlanId;
  const currentPlanName = effectivePlanId ? getSubscriptionPlanName(effectivePlanId) : undefined;
  const currentPlan = currentPlanName;

  // Determine the alternative plan
  const alternativePlan = currentPlan === SubscriptionPlan.Basic ? SubscriptionPlan.Standard : SubscriptionPlan.Basic;

  // Get features for current and alternative plans
  const currentPlanFeatures = currentPlan === SubscriptionPlan.Standard ? standardFeatures : basicFeatures;
  const alternativePlanFeatures = alternativePlan === SubscriptionPlan.Standard ? standardFeatures : basicFeatures;
  const handleConfirmChange = () => {
    setShowConfirmDialog(true);
  };
  const handleConfirmDialog = async () => {
    if (!customer?.id) {
      enqueueSnackbar(t('error.something'), {
        variant: 'error'
      });
      return;
    }
    setIsChanging(true);
    try {
      const newSubscriptionPlanId = getSubscriptionPlanId(alternativePlan);
      await SubscriptionsService.editSubscriptionForObe({
        body: {
          obeNumber: tag.obeNumber,
          subscriptionPlanId: newSubscriptionPlanId
        }
      });
      enqueueSnackbar(t('subscriptions.change_success'), {
        variant: 'success'
      });
      setObeDetailsRequestState(prev => prev + 1);
      setAgreementsRequestId(prev => prev + 1);
      // Navigate back to tag details after successful change
      push(TAG_DETAILS(tagNumber));
    } catch (error) {
      console.error('Error changing subscription plan:', error);
      enqueueSnackbar(t('error.change_subscription_plan'), {
        variant: 'error'
      });
    } finally {
      setIsChanging(false);
      setShowConfirmDialog(false);
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: container
  }, /*#__PURE__*/React.createElement(BackButton, {
    to: TAG_DETAILS(tagNumber)
  }, /*#__PURE__*/React.createElement(LargeHeader, null, t('subscriptions.change_subscription_plan'))), /*#__PURE__*/React.createElement(Box, {
    mt: 3
  }, /*#__PURE__*/React.createElement(Header, null, t('subscriptions.current_plan')), /*#__PURE__*/React.createElement(Paper, {
    className: currentPlanBox
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: 1
  }, /*#__PURE__*/React.createElement(Box, {
    display: 'flex',
    justifyContent: 'space-between'
  }, /*#__PURE__*/React.createElement("div", {
    className: planTitle
  }, t(`subscriptions.plans.${currentPlan?.toLowerCase()}`)), /*#__PURE__*/React.createElement(Box, {
    className: currentPlanBadge
  }, /*#__PURE__*/React.createElement(CheckCircle, null), /*#__PURE__*/React.createElement("span", null, t('subscriptions.current')))), /*#__PURE__*/React.createElement(Box, {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }, /*#__PURE__*/React.createElement("div", {
    className: planDescription
  }, t(`subscriptions.plans.${currentPlan?.toLowerCase()}_description`)), /*#__PURE__*/React.createElement("div", {
    className: planPrice
  }, t(`subscriptions.pricing.${currentPlan?.toLowerCase()}`))), /*#__PURE__*/React.createElement("ul", {
    className: featuresList
  }, currentPlanFeatures.map((featureKey, index) => /*#__PURE__*/React.createElement("li", {
    key: index,
    className: featureItem
  }, /*#__PURE__*/React.createElement(FiberManualRecord, {
    fontSize: "small"
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: featureKey,
    components: {
      bold: /*#__PURE__*/React.createElement("span", {
        style: {
          fontWeight: 'bold'
        }
      })
    }
  }))))))))), /*#__PURE__*/React.createElement(Box, {
    mt: 4
  }, /*#__PURE__*/React.createElement(Header, null, t('subscriptions.switch_to')), /*#__PURE__*/React.createElement(Paper, {
    className: alternativePlanBox
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Box, {
    display: 'flex',
    justifyContent: 'space-between'
  }, /*#__PURE__*/React.createElement("div", {
    className: planTitle
  }, t(`subscriptions.plans.${alternativePlan?.toLowerCase()}`)), /*#__PURE__*/React.createElement("div", {
    className: planPrice
  }, t(`subscriptions.pricing.${alternativePlan?.toLowerCase()}`))), /*#__PURE__*/React.createElement("div", {
    className: planDescription
  }, t(`subscriptions.plans.${alternativePlan?.toLowerCase()}_description`)), /*#__PURE__*/React.createElement("ul", {
    className: featuresList
  }, alternativePlanFeatures.map((featureKey, index) => /*#__PURE__*/React.createElement("li", {
    key: index,
    className: featureItem
  }, /*#__PURE__*/React.createElement(FiberManualRecord, {
    fontSize: "small"
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: featureKey,
    components: {
      bold: /*#__PURE__*/React.createElement("span", {
        style: {
          fontWeight: 'bold'
        }
      })
    }
  }))))))), currentPlan === SubscriptionPlan.Standard && alternativePlan === SubscriptionPlan.Basic && /*#__PURE__*/React.createElement(Box, {
    mt: 2
  }, /*#__PURE__*/React.createElement(Paper, {
    style: {
      padding: '16px',
      backgroundColor: '#ffebee',
      border: '1px solid #f44336',
      borderRadius: '4px'
    }
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "subscriptions.fee_warning.standard_to_basic",
    components: {
      red: /*#__PURE__*/React.createElement("span", {
        style: {
          color: '#f44336',
          fontWeight: 'bold'
        }
      })
    }
  }))), /*#__PURE__*/React.createElement(Box, {
    mt: 3
  }, /*#__PURE__*/React.createElement(PrimaryButton, {
    onClick: handleConfirmChange
  }, t('subscriptions.switch_to_plan', {
    plan: t(`subscriptions.plans.${alternativePlan?.toLowerCase()}`)
  })))), /*#__PURE__*/React.createElement(Dialog, {
    open: showConfirmDialog,
    onClose: () => setShowConfirmDialog(false),
    maxWidth: "md",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(DialogTitle, null, t('subscriptions.confirm_change_title')), /*#__PURE__*/React.createElement(DialogContent, {
    className: confirmDialog
  }, /*#__PURE__*/React.createElement(Box, {
    mb: 3
  }, /*#__PURE__*/React.createElement(Paper, {
    style: {
      padding: '16px',
      backgroundColor: '#f8f9fa'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement("strong", null, t('vehicles.vehicle'), ":"), ' ', tag.registrationNumber), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement("strong", null, t('tag.tag_number'), ":"), " ", tag.obeNumber)))), /*#__PURE__*/React.createElement(Box, {
    mb: 3
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: 2
  }, /*#__PURE__*/React.createElement(Paper, {
    style: {
      padding: '8px 16px',
      backgroundColor: '#e3f2fd',
      marginRight: '16px'
    }
  }, /*#__PURE__*/React.createElement("strong", null, t(`subscriptions.plans.${currentPlan?.toLowerCase()}`))), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: '20px',
      margin: '0 8px'
    }
  }, "\u2192"), /*#__PURE__*/React.createElement(Paper, {
    style: {
      padding: '8px 16px',
      backgroundColor: '#e8f5e8',
      marginLeft: '16px'
    }
  }, /*#__PURE__*/React.createElement("strong", null, t(`subscriptions.plans.${alternativePlan?.toLowerCase()}`))))), /*#__PURE__*/React.createElement(Box, {
    mb: 3
  }, /*#__PURE__*/React.createElement(Paper, {
    style: {
      padding: '20px',
      backgroundColor: '#fff3e0',
      border: '1px solid #ffcc02',
      borderRadius: '4px'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      lineHeight: '1.6',
      fontSize: '14px'
    }
  }, currentPlan === SubscriptionPlan.Standard && alternativePlan === SubscriptionPlan.Basic ? /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "subscriptions.confirmation.standard_to_basic",
    values: {
      vehicleRegNumber: tag.registrationNumber,
      tagNumber: tag.obeNumber,
      currentPlan: t(`subscriptions.plans.${currentPlan.toLowerCase()}`),
      newPlan: t(`subscriptions.plans.${alternativePlan.toLowerCase()}`)
    },
    components: {
      bold: /*#__PURE__*/React.createElement("strong", null),
      italic: /*#__PURE__*/React.createElement("em", null),
      br: /*#__PURE__*/React.createElement("br", null),
      a: /*#__PURE__*/React.createElement("a", {
        href: FLYT_NO_LINK,
        target: "_blank",
        rel: "noopener noreferrer",
        style: {
          color: '#1976d2',
          textDecoration: 'underline'
        }
      })
    }
  }) : /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "subscriptions.confirmation.basic_to_standard",
    values: {
      vehicleRegNumber: tag.registrationNumber,
      currentPlan: t(`subscriptions.plans.${currentPlan.toLowerCase()}`),
      newPlan: t(`subscriptions.plans.${alternativePlan.toLowerCase()}`)
    },
    components: {
      bold: /*#__PURE__*/React.createElement("strong", null),
      italic: /*#__PURE__*/React.createElement("em", null),
      br: /*#__PURE__*/React.createElement("br", null),
      a: /*#__PURE__*/React.createElement("a", {
        href: FLYT_NO_LINK,
        target: "_blank",
        rel: "noopener noreferrer",
        style: {
          color: '#1976d2',
          textDecoration: 'underline'
        }
      })
    }
  })))), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justifyContent: "flex-end",
    gridColumnGap: 16
  }, /*#__PURE__*/React.createElement(SecondaryButton, {
    onClick: () => setShowConfirmDialog(false)
  }, t('buttons.cancel')), /*#__PURE__*/React.createElement(PrimaryButton, {
    onClick: handleConfirmDialog,
    loading: isChanging
  }, t('buttons.confirm'))))));
}